import { Link } from "react-router-dom";
import NewsletterForm from "@/components/landing/NewsletterForm";

const COLUMNS = [
  {
    title: "Publisher",
    links: [
      { label: "About", href: "/#about" },
      { label: "Editorial Board Spotlight", href: "/#board-spotlight-section" },
      { label: "Initiatives · Evidentica", href: "/#evidentica" },
      { label: "Initiatives · BreastHub", href: "https://breasthub.net/", external: true },
      { label: "Initiatives · Nutramilo", href: "https://nutramilo.com/", external: true },
      { label: "Global Reach", href: "/#global" },
      { label: "Contact", href: "/#contact" },
    ],
  },
  {
    title: "Journals",
    links: [
      {
        label: "Int. Journal of Medical Reviews & Case Reports",
        href: "https://www.mdpub.net",
        external: true,
      },
      {
        label: "Int. Journal of Surgery and Medicine",
        href: "https://www.ejos.org",
        external: true,
      },
      { label: "Call for Papers", href: "/#call-for-papers" },
      { label: "Editorial Board · IJMRCR", to: "/editorial-board/ijmrcr" },
      { label: "Editorial Board · IJSM", to: "/editorial-board/ijsm" },
    ],
  },
  {
    title: "For Authors",
    links: [
      { label: "Submit · IJMRCR", href: "https://www.ejmanager.com/my/ijmrcr/", external: true },
      { label: "Submit · IJSM", href: "https://www.ejmanager.com/my/ijsm/", external: true },
      { label: "Peer Review", href: "/#standards" },
      { label: "Open Access", href: "/#standards" },
      { label: "Author Charter", href: "/#authors" },
    ],
  },
  {
    title: "Policies",
    links: [
      { label: "Editorial Policies", href: "/#standards" },
      { label: "Publication Ethics", href: "/#standards" },
      { label: "Accessibility", to: "/accessibility" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Use", to: "/terms" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="footer"
      className="relative bg-[var(--sci-navy)] text-white pt-20 md:pt-28 pb-12 overflow-hidden print:bg-white print:text-black"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none print:hidden" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top: mega brand mark */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border border-white/40 flex items-center justify-center">
                <span className="font-serif-display text-xl leading-none">S</span>
              </div>
              <div>
                <div className="font-serif-display text-xl leading-tight">
                  Sci Ink Press
                </div>
                <div className="overline text-white/55 text-[10px]">
                  International · Bulgaria
                </div>
              </div>
            </div>
            <p className="text-white/65 leading-relaxed max-w-md">
              International Sci Ink Press Ltd advances medical knowledge through
              transparent peer review, open access publishing, and global
              scientific collaboration.
            </p>

            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="overline text-white/45 mb-2">Leadership</div>
              <div className="text-sm text-white/90">
                <Link
                  to="/editor/ivan-inkov"
                  data-testid="footer-ceo-link"
                  className="font-serif-display text-lg text-white hover:text-[var(--sci-teal)] transition-colors leading-tight"
                >
                  Dr Ivan Inkov
                </Link>
                <div className="text-xs text-white/60 mt-1 leading-relaxed">
                  CEO &amp; Founder · International Sci Ink Press Ltd<br />
                  Editor-in-Chief · IJMRCR &amp; IJSM
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-3 text-sm">
              <div>
                <span className="overline text-white/45 mr-3">Reg. No.</span>
                <span className="font-mono-sci">205414288</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="overline text-white/45 mt-0.5">Address</span>
                <span className="text-white/90 leading-relaxed">
                  Antim I 57, fl. 1, Vazrazhdane District<br />
                  Sofia 1303, Bulgaria
                </span>
              </div>
              <div>
                <span className="overline text-white/45 mr-3">Contact</span>
                <span className="text-white/85">
                  Through the editorial portals at{" "}
                  <a
                    href="https://www.mdpub.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-contact-mdpub"
                    className="text-white hover:text-[var(--sci-teal)] underline decoration-dotted"
                  >
                    mdpub.net
                  </a>{" "}
                  ·{" "}
                  <a
                    href="https://www.ejos.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-contact-ejos"
                    className="text-white hover:text-[var(--sci-teal)] underline decoration-dotted"
                  >
                    ejos.org
                  </a>
                </span>
              </div>
              <div className="pt-3 flex flex-wrap gap-3 text-[11px] font-mono-sci tracking-wider">
                <a
                  href="https://www.portico.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-portico"
                  className="border border-white/20 px-2.5 py-1.5 text-white/75 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] transition-colors"
                >
                  ARCHIVED · PORTICO
                </a>
                <a
                  href="https://www.coalition-s.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-coalition-s"
                  className="border border-white/20 px-2.5 py-1.5 text-white/75 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] transition-colors"
                >
                  COALITION S · PLAN S
                </a>
                <a
                  href="https://www.equator-network.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-equator"
                  className="border border-white/20 px-2.5 py-1.5 text-white/75 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] transition-colors"
                >
                  EQUATOR NETWORK
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <div className="overline text-white/55 mb-5">{col.title}</div>
                <ul className="space-y-3">
                  {col.links.map((l) => {
                    const testid = `footer-link-${l.label
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")}`;
                    if (l.to) {
                      return (
                        <li key={l.label}>
                          <Link
                            to={l.to}
                            data-testid={testid}
                            className="text-sm text-white/75 hover:text-[var(--sci-teal)] transition-colors leading-snug block"
                          >
                            {l.label}
                          </Link>
                        </li>
                      );
                    }
                    return (
                      <li key={l.label}>
                        <a
                          href={l.href}
                          target={l.external ? "_blank" : undefined}
                          rel={l.external ? "noopener noreferrer" : undefined}
                          data-testid={testid}
                          className="text-sm text-white/75 hover:text-[var(--sci-teal)] transition-colors leading-snug block"
                        >
                          {l.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Footer-inline newsletter capture */}
        <div className="border-t border-white/10 pt-10 pb-8 mb-4 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-white/55 mb-3">Stay in the loop</div>
            <h3 className="font-serif-display text-2xl md:text-3xl text-white leading-tight">
              New DOIs. New collections.
            </h3>
            <p className="text-white/65 text-sm mt-3 max-w-md leading-relaxed">
              A short occasional brief from the publisher — newly registered DOIs across IJMRCR and IJSM, plus open Calls for Papers.
            </p>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm compact source="footer" testIdPrefix="newsletter-footer" />
          </div>
        </div>

        {/* Massive typographic brand mark */}
        <div className="border-t border-white/10 pt-12 mb-8 print:hidden">
          <div
            aria-hidden="true"
            className="font-serif-display tracking-tight leading-none text-white/[0.07] select-none text-[18vw] md:text-[14vw] lg:text-[180px] xl:text-[220px] whitespace-nowrap"
          >
            SCI INK PRESS
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div className="text-xs text-white/55 font-mono-sci">
            © {new Date().getFullYear()} INTERNATIONAL SCI INK PRESS LTD · ALL RIGHTS RESERVED
          </div>
          <div className="text-xs text-white/55 font-mono-sci">
            Made with editorial care in Sofia · Bulgaria
          </div>
        </div>
      </div>
    </footer>
  );
}
